import { Box, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { Point } from 'highcharts';
import { filter, get, isEmpty, pickBy } from 'lodash';
import numeral from 'numeral';
import queryString from 'query-string';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ButtonGroupFilter from 'src/components/common/ButtonGroupFilter';
import FlagCountry from 'src/components/common/FlagCountry';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import LinkToCompanyDetail, { IForwardedParamsCompanyDetailLink } from 'src/components/LinkToCompanyDetail';
import Table from 'src/components/Table';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryChartCTDCompanyAggregatedByBOL,
  useQueryListCTDCompanyAggregatedByBOL,
  useQueryMapCTDCompanyAggregatedByBOL,
} from 'src/services/queries/useQueryCompanyTradeData';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

interface ICompanyAggregatedByBOLProps {
  type: TypeBuyerSupplier;
  company: ICompanyDetail;
}

const CompanyAggregatedByBOL = (props: ICompanyAggregatedByBOLProps) => {
  const { type, company } = props;
  const { t } = useTranslation();
  const { id: companyId } = useParams();
  const theme = useTheme();
  const { checkRestriction } = useProvisionRestriction();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();

  const queryParams = useQueryParams();

  const [initDateFrom, initDateTo] = useMemo(() => {
    return [
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD'),
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD'),
    ];
  }, []);

  const [fromTime, setFromTime] = useState(
    queryParams.start_date || initDateFrom.startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
  );
  const [toTime, setToTime] = useState(queryParams.end_date || initDateTo.format(FORMAT_DATE_TIME.yyyy_mm_dd));
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(LIMIT_GET_LIST);

  const { setSort, ctdCompanyAggregatedByBOLSortParams } = useTradeDataSortStore();

  const [unitFilter, setUnitFilter] = useState<KeyFilterChartTradeDataUnitBy>(KeyFilterChartTradeDataUnitBy.bol);

  const chartParams = {
    limit,
    offset,
    start_date: dayjs(fromTime).startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    end_date: dayjs(toTime).endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    sort_by: unitFilter,
    order: 'desc',
    company_id: companyId,
  };

  const listSortField = useMemo(() => {
    if (ctdCompanyAggregatedByBOLSortParams?.sortBy === 'company_name') {
      return type === TypeBuyerSupplier.buyer ? 'buyer_name' : 'supplier_name';
    }
    return ctdCompanyAggregatedByBOLSortParams?.sortBy;
  }, [ctdCompanyAggregatedByBOLSortParams?.sortBy, type]);

  const listParams = {
    limit,
    offset,
    start_date: dayjs(fromTime).startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    end_date: dayjs(toTime).endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    sort_by: listSortField,
    order: ctdCompanyAggregatedByBOLSortParams?.sort,
    company_id: companyId,
  };

  const mapParams = {
    start_date: dayjs(fromTime).startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    end_date: dayjs(toTime).endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    company_id: companyId,
  };

  const listResp = useQueryListCTDCompanyAggregatedByBOL(companyId || '', type, listParams);
  const chartResp = useQueryChartCTDCompanyAggregatedByBOL(companyId || '', type, chartParams);
  const mapResp = useQueryMapCTDCompanyAggregatedByBOL(companyId || '', type, mapParams);

  const nameField = `company_name` as keyof ICompanyAggregatedByBOL;
  const countryCodeField = `company_country_code` as keyof ICompanyAggregatedByBOL;
  const percentField = `percentage_${unitFilter}` as keyof ICompanyAggregatedByBOL;
  const idField = `company_id` as keyof ICompanyAggregatedByBOL;

  const worldMapFieldNameMapping = {
    [TypeBuyerSupplier.supplier]: {
      countryCodeField: 'supplier_country_code',
      valueField: 'suppliers',
    },
    [TypeBuyerSupplier.buyer]: {
      countryCodeField: 'buyer_country_code',
      valueField: 'buyers',
    },
  };

  const unitFieldMapping = {
    supplier: {
      label: 'Suppliers',
      field: 'suppliers',
    },
    buyer: {
      label: 'Customers',
      field: 'buyers',
    },
  };

  const chartLabelByUnitFilter = unitFieldMapping[type].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[type].field;

  const worldMapData = useMemo(() => {
    return mapResp?.data?.data?.map((item) => {
      return [
        get(item, worldMapFieldNameMapping[type].countryCodeField, '').toLowerCase(),
        get(item, worldMapFieldNameMapping[type].valueField, 0),
      ];
    }) as Array<[string, number | null]>;
  }, [mapResp?.data?.data, chartFieldNameByUnitFilter]);

  const configColumn: ConfigCell<ICompanyAggregatedByBOL>[] = [
    {
      disablePadding: false,
      id: 'company_name',
      disableSort: true,
      label:
        type === TypeBuyerSupplier.supplier
          ? t('trade_data.buyers.table.suppliers')
          : t('trade_data.buyers.table.customers'),
      cell: (data: ICompanyAggregatedByBOL) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={(data?.[countryCodeField] as string) || ''} />
            <LinkToCompanyDetail
              id={data?.[idField] as string}
              companyName={(data?.[nameField] as string) || ''}
              params={queryParams}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'percentage',
      label: 'Proportion (%)',
      disableSort: true,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(data[`percentage_${unitFilter}` as keyof ICompanyAggregatedByBOL])}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.bol).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.weight).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.quantity).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      width: 200,
      cell: (data: ICompanyAggregatedByBOL) => {
        const checkRestrictionValue = checkRestriction({
          countryCode:
            type === TypeBuyerSupplier.supplier ? data?.supplier_country_code || '' : data?.buyer_country_code || '',
          typeData: LIST_RESTRICTION_DATA.VALUES_USD,
        });
        return (
          <Typography variant="body2" color="text.secondary">
            {formatValueUSD(data.value_usd)}
            {checkRestrictionValue && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
  ];

  const renderChart = () => {
    const data = chartResp.data.data;
    const valueChart = getArrayDefault(data)?.map((item: ICompanyAggregatedByBOL, index) => ({
      name: item[idField] as string,
      value: Math.cbrt(Number(get(item, percentField, ''))),
      sortIndex: index,
    }));

    if (isEmpty(filter(valueChart, (valueItem) => valueItem.value !== 0)) && !chartResp.isFetching) {
      return (
        <CompanyInfoNoData
          title={t('trade_data.common.notice_no_data.title')}
          content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
          sxContainer={{
            borderWidth: 0,
          }}
        />
      );
    }
    return (
      <TreemapChart
        colors={TREEMAP_PALLET_COLOR}
        height={600}
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = data.find((item: ICompanyAggregatedByBOL) => item[idField] === point?.name);
          const countryCode = transactionDetail?.[countryCodeField] as string;
          const countryName = transactionDetail?.[nameField];
          const value = numeral(get(transactionDetail, unitFilter, 0)).format('O,O');
          const valuePercent = transactionDetail?.[percentField];
          const flag = getEmojiFlag(countryCode || '');
          return `
            <div style="min-width: 270px">
              <span style="font-size: 24px; color: inherit; display: block; color: inherit; fontWeight: 600; margin-bottom: 5px">
                ${valuePercent}%
              </span>
              <span style="color: inherit; display: block; color: inherit; fontWeight: 600">
              ${flag} ${countryName || t('global.others')}
              </span>
              <span style="color: inherit; display: block; color: ${theme.palette.text.secondary}">${t('trade_data.filter_chart.unit.' + unitFilter)} <b style="color: #fff">
              ${numeral(value).format('O,O')}</b>
              </span>
            </div>`;
        }}
        dataLabelFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = data.find((item: ICompanyAggregatedByBOL) => item[idField] === point?.name);
          const countryCode = transactionDetail?.[countryCodeField] as string;
          const countryName = transactionDetail?.[nameField];
          const flag = getEmojiFlag(countryCode || '');
          const forwardedParams: IForwardedParamsCompanyDetailLink = pickBy(
            {
              start_date: queryParams?.start_date,
              end_date: queryParams?.end_date,
              viewedAsType: 'supplier',
            },
            Boolean,
          );
          const query = '?' + queryString.stringify(forwardedParams);

          const url = transactionDetail?.[idField]
            ? AuthRouteKeys.CompanyDetail.replace(':id?', (transactionDetail?.[idField] as string) || '') + query
            : '';
          return `<div style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}">
          <a style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}" href="${url}">
            <p style="display: block">${flag}</p>
            <p style="display: block">${countryName}</p>
          </a>
            </div>`;
        }}
        data={valueChart}
        loading={listResp.isFetching}
      />
    );
  };

  const isDataReturnedEmpty =
    !listResp.isFetching &&
    !chartResp.isFetching &&
    !mapResp.isFetching &&
    isEmpty(getArrayDefault(listResp?.data?.data)) &&
    isEmpty(getArrayDefault(chartResp?.data?.data)) &&
    isEmpty(getArrayDefault(mapResp?.data?.data));

  return (
    <Stack direction="column" spacing={3}>
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography gap={1} display={'flex'} variant="h5">
              Where are {type === TypeBuyerSupplier.buyer ? 'Customers' : 'Suppliers'} of
              <Typography variant="h5" color={'secondary.main'}>
                {company.name}
              </Typography>
              Located?
            </Typography>
          </Stack>
          <Stack maxWidth={300} direction={'row'} alignItems={'center'} gap={1}>
            <DatePickerWithDefaultIcon
              value={dayjs(fromTime)}
              disableFuture={true}
              onChange={(value) => setFromTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd))}
              label={t('trade_data.search_form.input.from')}
              format={FORMAT_DATE_TIME.MONTH_YEAR}
              slotProps={{
                textField: {
                  size: 'small',
                  required: true,
                },
              }}
              minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              views={['month', 'year']}
            />
            -
            <DatePickerWithDefaultIcon
              value={dayjs(toTime)}
              disableFuture={true}
              format={FORMAT_DATE_TIME.MONTH_YEAR}
              onChange={(value) => setToTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd))}
              label={t('trade_data.search_form.input.trade_to')}
              slotProps={{
                textField: {
                  size: 'small',
                  required: true,
                },
              }}
              minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              views={['month', 'year']}
            />
          </Stack>
          {!isDataReturnedEmpty ? (
            <WorldMapChart
              tooltipFormatter={function () {
                const point: Point = this.point;
                const flag = getEmojiFlag(point.properties['hc-key']);
                return `<div>
            <b>${flag} ${point.name}</b>
            <br/><b style="color: ${theme.palette.text.secondary}">${chartLabelByUnitFilter} </b> <b>${renderNumberOrDefault(point.value)}</b>
          </div>`;
              }}
              data={worldMapData}
              height={580}
              type={WorldMapOptions.customer}
            />
          ) : (
            <CompanyInfoNoData
              title={t('trade_data.common.notice_no_data.title')}
              content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
            />
          )}
        </Stack>
      </WhiteBoxContent>
      {!isDataReturnedEmpty && (
        <WhiteBoxContent display={'flex'} flexDirection={'column'} gap={3}>
          <Stack justifyContent={'space-between'} flexDirection={'row'}>
            <Typography textAlign={'left'} variant="h5">
              {type === TypeBuyerSupplier.supplier
                ? t('trade_data.suppliers_proportion')
                : t('trade_data.customers_proportion')}
            </Typography>
            <ButtonGroupFilter
              size="small"
              label="Unit By"
              buttons={[
                {
                  label: 'No. of B/L',
                  key: KeyFilterChartTradeDataUnitBy.bol,
                },
                {
                  label: 'Weight',
                  key: KeyFilterChartTradeDataUnitBy.weight,
                },
                {
                  label: 'Quantity',
                  key: KeyFilterChartTradeDataUnitBy.quantity,
                },
                {
                  label: 'USD',
                  key: KeyFilterChartTradeDataUnitBy.value_usd,
                },
              ]}
              active={unitFilter}
              onClick={(value) => {
                setUnitFilter(value as any);
                setSort('ctdCompanyAggregatedByBOLSortParams', {
                  sortBy: value,
                  sort: 'desc',
                });
              }}
            />
          </Stack>
          {renderChart()}
        </WhiteBoxContent>
      )}
      {!isDataReturnedEmpty && (
        <WhiteBoxContent>
          <Stack direction="column" spacing={3}>
            <Typography textAlign="left" variant="h5">
              {type === TypeBuyerSupplier.supplier
                ? t('trade_data.buyers_country.suppliers_list')
                : t('trade_data.buyers_country.customers_list')}
            </Typography>
            <Table
              maxRow={10}
              onChangePage={(value) => {
                setOffset(value);
              }}
              onChangePageSize={(value) => {
                setOffset(0);
                setLimit(value);
              }}
              handleSort={(data) => {
                setSort('ctdCompanyAggregatedByBOLSortParams', data);
              }}
              loading={listResp.isFetching}
              page={listResp.data.page}
              pageSize={listResp.data.page_size}
              totalRecords={listResp.data.total_records}
              dataKey={'supplier_id'}
              data={listResp.data.data}
              configColumn={configColumn}
            />
          </Stack>
        </WhiteBoxContent>
      )}
    </Stack>
  );
};

export default CompanyAggregatedByBOL;
